import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, CheckCircle, XCircle, Video, List, ChevronDown } from 'lucide-react';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  dashboardService,
  AppointmentWithDetails,
} from '../../services/dashboard/dashboardService';
import { videoConferenceService, CanJoinCallResponse } from '../../services/video-conference/videoConferenceService';
import { formatDateTime, formatDuration } from '../../utils/dateUtils';
import { getAppointmentStatusBadgeClass, getAppointmentStatusLabel } from '../../utils/statusUtils';
import { getConsultationTypeLabel } from '../../utils/appointmentUtils';
import { getInitials } from '../../utils/userUtils';
import './AppointmentsPage.css';

type AppointmentTab = 'all' | 'upcoming' | 'past' | 'missed_cancelled' | 'reschedule_pending';
type ViewMode = 'list' | 'calendar';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const TABS: AppointmentTab[] = ['all', 'upcoming', 'past', 'missed_cancelled', 'reschedule_pending'];
const VIEW_MODES: ViewMode[] = ['list', 'calendar'];

const STORAGE_KEY_ACTIVE_TAB = 'appointmentsPage_activeTab';
const STORAGE_KEY_VIEW_MODE = 'appointmentsPage_viewMode';
const STORAGE_KEY_CALENDAR_MONTH = 'appointmentsPage_calendarMonth';
const STORAGE_KEY_SELECTED_CALENDAR_DATE = 'appointmentsPage_selectedCalendarDate';
const STORAGE_KEY_CALENDAR_FILTER = 'appointmentsPage_calendarFilter';
const STORAGE_KEY_CALENDAR_DAY_TAB = 'appointmentsPage_calendarDayTab';

const loadStoredActiveTab = (): AppointmentTab => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ACTIVE_TAB);
    if (raw && TABS.includes(raw as AppointmentTab)) return raw as AppointmentTab;
  } catch {
    // ignore
  }
  return 'upcoming';
};

const loadStoredViewMode = (): ViewMode => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_VIEW_MODE);
    if (raw && VIEW_MODES.includes(raw as ViewMode)) return raw as ViewMode;
  } catch {
    // ignore
  }
  return 'list';
};

const loadStoredCalendarMonth = (): Date => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CALENDAR_MONTH);
    if (raw && /^\d{4}-\d{2}$/.test(raw)) {
      const [y, m] = raw.split('-').map(Number);
      const d = new Date(y, m - 1, 1);
      if (!Number.isNaN(d.getTime())) return d;
    }
  } catch {
    // ignore
  }
  return new Date();
};

const loadStoredSelectedCalendarDate = (): string | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SELECTED_CALENDAR_DATE);
    if (raw === null || raw === '') return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  } catch {
    // ignore
  }
  return null;
};

const loadStoredCalendarFilter = (): AppointmentTab => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CALENDAR_FILTER);
    if (raw && TABS.includes(raw as AppointmentTab)) return raw as AppointmentTab;
  } catch {
    // ignore
  }
  return 'upcoming';
};

const loadStoredCalendarDayTab = (): AppointmentTab => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CALENDAR_DAY_TAB);
    if (raw && TABS.includes(raw as AppointmentTab)) return raw as AppointmentTab;
  } catch {
    // ignore
  }
  return 'upcoming';
};

const AppointmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [appointments, setAppointments] = useState<AppointmentWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AppointmentTab>(loadStoredActiveTab);
  const [viewMode, setViewMode] = useState<ViewMode>(loadStoredViewMode);
  const [calendarMonth, setCalendarMonth] = useState<Date>(loadStoredCalendarMonth);
  const [allAppointmentsForCalendar, setAllAppointmentsForCalendar] = useState<AppointmentWithDetails[]>([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(loadStoredSelectedCalendarDate);
  const [calendarDayTab, setCalendarDayTab] = useState<AppointmentTab>(loadStoredCalendarDayTab);
  const [calendarFilter, setCalendarFilter] = useState<AppointmentTab>(loadStoredCalendarFilter);
  const [calendarFilterOpen, setCalendarFilterOpen] = useState(false);
  const calendarFilterRef = useRef<HTMLDivElement>(null);
  const [canJoinStatuses, setCanJoinStatuses] = useState<Map<number, CanJoinCallResponse>>(new Map());
  const [loadingCanJoin, setLoadingCanJoin] = useState<Set<number>>(new Set());

  // Load can-join status for an appointment from backend
  const loadCanJoinStatus = useCallback(async (appointmentId: number, forceRefresh = false) => {
    if (loadingCanJoin.has(appointmentId)) {
      return; // Already loading
    }
    if (!forceRefresh && canJoinStatuses.has(appointmentId)) {
      return; // Already loaded (unless forcing refresh)
    }
    
    try {
      setLoadingCanJoin(prev => new Set(prev).add(appointmentId));
      const response = await videoConferenceService.canJoinCallForAppointment(appointmentId);
      setCanJoinStatuses(prev => new Map(prev).set(appointmentId, response));
    } catch (error) {
      console.error(`Failed to load can-join status for appointment ${appointmentId}:`, error);
      // On error, default to not allowing join
      setCanJoinStatuses(prev => new Map(prev).set(appointmentId, { can_join: false, message: 'Unable to check join status' }));
    } finally {
      setLoadingCanJoin(prev => {
        const next = new Set(prev);
        next.delete(appointmentId);
        return next;
      });
    }
  }, [loadingCanJoin, canJoinStatuses]);

  // Get can-join status for an appointment (from backend response)
  const canJoinCall = useCallback((appointmentId: number): boolean => {
    const status = canJoinStatuses.get(appointmentId);
    return status?.can_join ?? false;
  }, [canJoinStatuses]);

  // Get join message for an appointment (from backend response)
  const getJoinMessage = useCallback((appointmentId: number): string | undefined => {
    const status = canJoinStatuses.get(appointmentId);
    return status?.message;
  }, [canJoinStatuses]);

  const loadAppointments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const allAppointments = await dashboardService.getAppointments(undefined, 500, 0);
      const now = new Date();

      if (activeTab === 'all') {
        const sorted = allAppointments.sort((a, b) => {
          const dateA = new Date(a.appointment_date || 0);
          const dateB = new Date(b.appointment_date || 0);
          return dateB.getTime() - dateA.getTime(); // Most recent first
        });
        setAppointments(sorted);
        sorted.forEach(apt => {
          if (apt.status === 'scheduled' || apt.status === 'confirmed') {
            loadCanJoinStatus(apt.id);
          }
        });
        setLoading(false);
        return;
      }

      if (activeTab === 'upcoming') {
        const filtered = allAppointments.filter((apt) => {
          if (!apt.appointment_date) return false;
          const aptDate = new Date(apt.appointment_date);
          const durationMinutes = apt.duration_minutes ?? apt.duration ?? 30;
          const appointmentEndTime = new Date(aptDate.getTime() + durationMinutes * 60 * 1000);
          const isFuture = appointmentEndTime > now;
          const status = apt.status?.toLowerCase() || '';
          return isFuture && status !== 'missed' && status !== 'cancelled' && status !== 'completed';
        });
        const sorted = filtered.sort((a, b) => {
          const dateA = new Date(a.appointment_date || 0);
          const dateB = new Date(b.appointment_date || 0);
          return dateA.getTime() - dateB.getTime();
        });
        setAppointments(sorted);
        sorted.forEach(apt => {
          if (apt.status === 'scheduled' || apt.status === 'confirmed') {
            loadCanJoinStatus(apt.id);
          }
        });
        setLoading(false);
        return;
      }

      if (activeTab === 'past') {
        const filtered = allAppointments.filter((apt) => (apt.status?.toLowerCase() || '') === 'completed');
        setAppointments(filtered.sort((a, b) => {
          const dateA = new Date(a.appointment_date || 0);
          const dateB = new Date(b.appointment_date || 0);
          return dateB.getTime() - dateA.getTime();
        }));
        setLoading(false);
        return;
      }

      if (activeTab === 'missed_cancelled') {
        const filtered = allAppointments.filter(
          (apt) => apt.status === 'cancelled' || apt.status === 'missed'
        );
        setAppointments(filtered.sort((a, b) => {
          const dateA = new Date(a.appointment_date || 0);
          const dateB = new Date(b.appointment_date || 0);
          return dateB.getTime() - dateA.getTime();
        }));
        setLoading(false);
        return;
      }

      if (activeTab === 'reschedule_pending') {
        const filtered = allAppointments.filter(
          (apt) => (apt.status?.toLowerCase() || '') === 'reschedule_pending'
        );
        setAppointments(filtered.sort((a, b) => {
          const dateA = new Date(a.appointment_date || 0);
          const dateB = new Date(b.appointment_date || 0);
          return dateA.getTime() - dateB.getTime();
        }));
        setLoading(false);
        return;
      }

      setAppointments([]);
    } catch (err: any) {
      console.error('Failed to load appointments:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [activeTab, loadCanJoinStatus]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Load all appointments when switching to calendar view
  const loadCalendarAppointments = useCallback(async () => {
    try {
      setLoadingCalendar(true);
      const all = await dashboardService.getAppointments(undefined, 500, 0);
      setAllAppointmentsForCalendar(all);
    } catch (err) {
      console.error('Failed to load appointments for calendar', err);
    } finally {
      setLoadingCalendar(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'calendar') {
      loadCalendarAppointments();
    }
  }, [viewMode, loadCalendarAppointments]);

  // Filter calendar counts by dropdown (same logic as list view tabs); default Upcoming
  const calendarFilteredAppointments = useMemo(() => {
    const now = new Date();
    if (calendarFilter === 'all') return allAppointmentsForCalendar;
    if (calendarFilter === 'upcoming') {
      return allAppointmentsForCalendar.filter((apt) => {
        if (!apt.appointment_date) return false;
        const aptDate = new Date(apt.appointment_date);
        const durationMinutes = apt.duration_minutes ?? apt.duration ?? 30;
        const endTime = new Date(aptDate.getTime() + durationMinutes * 60 * 1000);
        const status = apt.status?.toLowerCase() || '';
        return endTime > now && status !== 'missed' && status !== 'cancelled' && status !== 'completed';
      });
    }
    if (calendarFilter === 'past') {
      return allAppointmentsForCalendar.filter((apt) => (apt.status?.toLowerCase() || '') === 'completed');
    }
    if (calendarFilter === 'missed_cancelled') {
      return allAppointmentsForCalendar.filter(
        (apt) => apt.status === 'cancelled' || apt.status === 'missed'
      );
    }
    if (calendarFilter === 'reschedule_pending') {
      return allAppointmentsForCalendar.filter(
        (apt) => (apt.status?.toLowerCase() || '') === 'reschedule_pending'
      );
    }
    return allAppointmentsForCalendar;
  }, [allAppointmentsForCalendar, calendarFilter]);

  // Count appointments per day for the visible month (based on dropdown filter)
  const countsByDate = useMemo(() => {
    const map: Record<string, number> = {};
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    calendarFilteredAppointments.forEach((apt) => {
      if (!apt.appointment_date) return;
      const d = new Date(apt.appointment_date);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const key = getDateKey(d);
        map[key] = (map[key] || 0) + 1;
      }
    });
    return map;
  }, [calendarFilteredAppointments, calendarMonth]);

  // Total in the visible month (based on dropdown filter)
  const monthTotal = useMemo(
    () => Object.values(countsByDate).reduce((a, b) => a + b, 0),
    [countsByDate]
  );

  const calendarFilterLabel = calendarFilter === 'missed_cancelled' ? 'Missed & Cancelled' : calendarFilter === 'reschedule_pending' ? 'Reschedule Pending' : calendarFilter.charAt(0).toUpperCase() + calendarFilter.slice(1);

  const calendarFilterOptions: { value: AppointmentTab; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'past', label: 'Past' },
    { value: 'missed_cancelled', label: 'Missed & Cancelled' },
    { value: 'reschedule_pending', label: 'Reschedule Pending' },
  ];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (calendarFilterRef.current && !calendarFilterRef.current.contains(e.target as Node)) {
        setCalendarFilterOpen(false);
      }
    };
    if (calendarFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [calendarFilterOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_ACTIVE_TAB, activeTab);
      localStorage.setItem(STORAGE_KEY_VIEW_MODE, viewMode);
      const y = calendarMonth.getFullYear();
      const m = String(calendarMonth.getMonth() + 1).padStart(2, '0');
      localStorage.setItem(STORAGE_KEY_CALENDAR_MONTH, `${y}-${m}`);
      localStorage.setItem(STORAGE_KEY_SELECTED_CALENDAR_DATE, selectedCalendarDate ?? '');
      localStorage.setItem(STORAGE_KEY_CALENDAR_FILTER, calendarFilter);
      localStorage.setItem(STORAGE_KEY_CALENDAR_DAY_TAB, calendarDayTab);
    } catch {
      // ignore
    }
  }, [activeTab, viewMode, calendarMonth, selectedCalendarDate, calendarFilter, calendarDayTab]);

  // Calendar grid: weeks (rows) and days (cells); empty cells for padding
  const calendarWeeks = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startDay = first.getDay();
    const daysInMonth = last.getDate();
    const cells: (number | null)[] = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) for (let i = 0; i < remaining; i++) cells.push(null);
    const weeks: (number | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
  }, [calendarMonth]);

  const prevMonth = () => {
    setCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1));
  };
  const nextMonth = () => {
    setCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1));
  };

  // Appointments for the selected calendar date
  const appointmentsForSelectedDate = useMemo(() => {
    if (!selectedCalendarDate || !allAppointmentsForCalendar.length) return [];
    return allAppointmentsForCalendar
      .filter((apt) => {
        if (!apt.appointment_date) return false;
        return getDateKey(new Date(apt.appointment_date)) === selectedCalendarDate;
      })
      .sort((a, b) => new Date(a.appointment_date || 0).getTime() - new Date(b.appointment_date || 0).getTime());
  }, [selectedCalendarDate, allAppointmentsForCalendar]);

  // Reset calendar day tab when selected date changes
  useEffect(() => {
    setCalendarDayTab('upcoming');
  }, [selectedCalendarDate]);

  // Filter calendar day list by tab (same segmentation as list view)
  const filteredCalendarDayAppointments = useMemo(() => {
    const now = new Date();
    if (calendarDayTab === 'all') return appointmentsForSelectedDate;
    if (calendarDayTab === 'upcoming') {
      return appointmentsForSelectedDate.filter((apt) => {
        if (!apt.appointment_date) return false;
        const aptDate = new Date(apt.appointment_date);
        const durationMinutes = apt.duration_minutes ?? apt.duration ?? 30;
        const endTime = new Date(aptDate.getTime() + durationMinutes * 60 * 1000);
        const status = apt.status?.toLowerCase() || '';
        return endTime > now && status !== 'missed' && status !== 'cancelled' && status !== 'completed';
      });
    }
    if (calendarDayTab === 'past') {
      return appointmentsForSelectedDate.filter((apt) => (apt.status?.toLowerCase() || '') === 'completed');
    }
    if (calendarDayTab === 'missed_cancelled') {
      return appointmentsForSelectedDate.filter(
        (apt) => apt.status === 'cancelled' || apt.status === 'missed'
      );
    }
    if (calendarDayTab === 'reschedule_pending') {
      return appointmentsForSelectedDate.filter(
        (apt) => (apt.status?.toLowerCase() || '') === 'reschedule_pending'
      );
    }
    return appointmentsForSelectedDate;
  }, [appointmentsForSelectedDate, calendarDayTab]);

  // Load can-join for selected date's appointments when shown
  useEffect(() => {
    if (viewMode !== 'calendar' || !selectedCalendarDate) return;
    appointmentsForSelectedDate.forEach((apt) => {
      if (apt.status === 'scheduled' || apt.status === 'confirmed') {
        loadCanJoinStatus(apt.id);
      }
    });
  }, [viewMode, selectedCalendarDate, appointmentsForSelectedDate, loadCanJoinStatus]);

  // Periodically refresh can-join status for upcoming appointments
  useEffect(() => {
    if (activeTab !== 'upcoming' && activeTab !== 'all') return;
    
    const interval = setInterval(() => {
      appointments.forEach(apt => {
        if ((apt.status === 'scheduled' || apt.status === 'confirmed') && !loadingCanJoin.has(apt.id)) {
          loadCanJoinStatus(apt.id, true); // Force refresh every 30 seconds
        }
      });
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, activeTab, loadingCanJoin, loadCanJoinStatus]);

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const sidebarItems = createSidebarItems(navigate, location.pathname);

  // Calculate insights
  const calculateInsights = async () => {
    try {
      const allAppointments = await dashboardService.getAppointments();
      
      const total = allAppointments.length;
      const completed = allAppointments.filter((apt) => apt.status === 'completed').length;
      const missedCancelled = allAppointments.filter(
        (apt) => apt.status === 'cancelled' || apt.status === 'missed'
      ).length;

      return { total, completed, missedCancelled };
    } catch (err) {
      console.error('Failed to calculate insights:', err);
      return { total: 0, completed: 0, missedCancelled: 0 };
    }
  };

  const [insights, setInsights] = useState({ total: 0, completed: 0, missedCancelled: 0 });

  useEffect(() => {
    const loadInsights = async () => {
      const data = await calculateInsights();
      setInsights(data);
    };
    loadInsights();
  }, []);


  const handleEditAppointment = (appointmentId: number) => {
    navigate(`/appointments/${appointmentId}/edit`);
  };

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />

        <div className="dashboard-container">
          <main className="dashboard-main-content">
            {loading && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                Loading appointments...
              </div>
            )}

            {error && (
              <div style={{ padding: '20px', background: '#fee2e2', borderRadius: '8px', color: '#b91c1c', marginBottom: '24px' }}>
                Error: {error}
              </div>
            )}

            {!loading && !error && (
              <div className="appointments-page-wrap">
                <div className="appointments-one-card">
                  {/* Appointment Insights */}
                  <section className="appointments-insights-section">
                    <h2 className="appointments-card-title">Appointment Insights</h2>
                    <div className="appointments-insights">
                      <div className="insight-card">
                        <div className="insight-icon insight-icon-total">
                          <Calendar size={20} strokeWidth={2} />
                        </div>
                        <div className="insight-content">
                          <div className="insight-label">Total Appointments</div>
                          <div className="insight-value">{insights.total}</div>
                        </div>
                      </div>
                      <div className="insight-card">
                        <div className="insight-icon insight-icon-completed">
                          <CheckCircle size={20} strokeWidth={2} />
                        </div>
                        <div className="insight-content">
                          <div className="insight-label">Completed</div>
                          <div className="insight-value">{insights.completed}</div>
                        </div>
                      </div>
                      <div className="insight-card">
                        <div className="insight-icon insight-icon-cancelled">
                          <XCircle size={20} strokeWidth={2} />
                        </div>
                        <div className="insight-content">
                          <div className="insight-label">Missed & Cancelled</div>
                          <div className="insight-value">{insights.missedCancelled}</div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Appointments Table Section */}
                  <section className="appointments-list-section">
                    <div className="appointments-section-header">
                      <h2 className="appointments-card-title">Appointments</h2>
                      <button
                        type="button"
                        className={`appointments-view-toggle ${viewMode === 'calendar' ? 'active' : ''}`}
                        onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
                        title={viewMode === 'calendar' ? 'Show list view' : 'Show calendar view'}
                        aria-label={viewMode === 'calendar' ? 'Show list view' : 'Show calendar view'}
                      >
                        {viewMode === 'calendar' ? (
                          <>
                            <List size={18} strokeWidth={2} aria-hidden />
                            <span className="appointments-view-toggle-label">List View</span>
                          </>
                        ) : (
                          <>
                            <Calendar size={18} strokeWidth={2} aria-hidden />
                            <span className="appointments-view-toggle-label">Calendar</span>
                          </>
                        )}
                      </button>
                    </div>

            {viewMode === 'calendar' ? (
              <div className="appointments-calendar-view">
                {loadingCalendar ? (
                  <div className="appointments-calendar-loading">Loading calendar...</div>
                ) : (
                  <>
                    <div className="appointments-calendar-nav">
                      <button type="button" className="appointments-calendar-nav-btn" onClick={prevMonth} aria-label="Previous month">
                        ‹
                      </button>
                      <h3 className="appointments-calendar-month-title">
                        {MONTH_NAMES[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                      </h3>
                      <button type="button" className="appointments-calendar-nav-btn" onClick={nextMonth} aria-label="Next month">
                        ›
                      </button>
                    </div>
                    <div className="appointments-calendar-month-total">
                      <span className="appointments-calendar-filter-label">Show:</span>
                      <div className="appointments-calendar-filter-wrap" ref={calendarFilterRef}>
                        <button
                          type="button"
                          className="appointments-calendar-filter-dropdown"
                          onClick={() => setCalendarFilterOpen((o) => !o)}
                          aria-expanded={calendarFilterOpen}
                          aria-haspopup="listbox"
                          aria-label="Filter calendar by appointment status"
                        >
                          <span className="appointments-calendar-filter-dropdown-value">{calendarFilterLabel}</span>
                          <ChevronDown size={18} className={`appointments-calendar-filter-chevron ${calendarFilterOpen ? 'open' : ''}`} aria-hidden />
                        </button>
                        {calendarFilterOpen && (
                          <ul
                            className="appointments-calendar-filter-menu"
                            role="listbox"
                            aria-label="Filter options"
                          >
                            {calendarFilterOptions.map((opt) => (
                              <li key={opt.value} role="option" aria-selected={calendarFilter === opt.value}>
                                <button
                                  type="button"
                                  className={`appointments-calendar-filter-option ${calendarFilter === opt.value ? 'selected' : ''}`}
                                  onClick={() => {
                                    setCalendarFilter(opt.value);
                                    setCalendarFilterOpen(false);
                                  }}
                                >
                                  {opt.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <span className="appointments-calendar-month-total-label">{calendarFilterLabel} this month:</span>
                      <span className="appointments-calendar-month-total-value">{monthTotal}</span>
                    </div>
                    <div className="appointments-calendar-grid">
                      <div className="appointments-calendar-weekdays">
                        {WEEKDAY_LABELS.map((label) => (
                          <div key={label} className="appointments-calendar-weekday">{label}</div>
                        ))}
                      </div>
                      {calendarWeeks.map((week, wi) => (
                        <div key={wi} className="appointments-calendar-week">
                          {week.map((day, di) => {
                            if (day === null) {
                              return <div key={di} className="appointments-calendar-day appointments-calendar-day-empty" />;
                            }
                            const dateKey = getDateKey(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day));
                            const count = countsByDate[dateKey] ?? 0;
                            const isToday = (() => {
                              const t = new Date();
                              return t.getFullYear() === calendarMonth.getFullYear() &&
                                t.getMonth() === calendarMonth.getMonth() && t.getDate() === day;
                            })();
                            const isSelected = selectedCalendarDate === dateKey;
                            return (
                              <div
                                key={di}
                                role="button"
                                tabIndex={0}
                                className={`appointments-calendar-day ${count > 0 ? 'has-appointments' : ''} ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}`}
                                onClick={() => setSelectedCalendarDate(dateKey)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedCalendarDate(dateKey); } }}
                                aria-label={count > 0 ? `${day}, ${count} appointment(s). Select to view list` : `Day ${day}`}
                              >
                                <span className="appointments-calendar-day-num">{day}</span>
                                {count > 0 && (
                                  <span className="appointments-calendar-day-count" title={`${count} appointment(s)`}>
                                    {count}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>

                    {selectedCalendarDate && (
                      <div className="appointments-calendar-day-list">
                        <h3 className="appointments-calendar-day-list-title">
                          Appointments on {(() => {
                            const [y, m, d] = selectedCalendarDate.split('-').map(Number);
                            return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                          })()}
                        </h3>
                        {/* Same tabs as list view: segment by Scheduled, Cancelled, etc. */}
                        <div className="appointments-tabs appointments-calendar-day-tabs">
                          <button
                            className={`appointment-tab ${calendarDayTab === 'all' ? 'active' : ''}`}
                            onClick={() => setCalendarDayTab('all')}
                          >
                            All
                          </button>
                          <button
                            className={`appointment-tab ${calendarDayTab === 'upcoming' ? 'active' : ''}`}
                            onClick={() => setCalendarDayTab('upcoming')}
                          >
                            Upcoming
                          </button>
                          <button
                            className={`appointment-tab ${calendarDayTab === 'past' ? 'active' : ''}`}
                            onClick={() => setCalendarDayTab('past')}
                          >
                            Past
                          </button>
                          <button
                            className={`appointment-tab ${calendarDayTab === 'missed_cancelled' ? 'active' : ''}`}
                            onClick={() => setCalendarDayTab('missed_cancelled')}
                          >
                            Missed & Cancelled
                          </button>
                          <button
                            className={`appointment-tab ${calendarDayTab === 'reschedule_pending' ? 'active' : ''}`}
                            onClick={() => setCalendarDayTab('reschedule_pending')}
                          >
                            Reschedule Pending
                          </button>
                        </div>
                        {appointmentsForSelectedDate.length === 0 ? (
                          <p className="appointments-calendar-day-list-empty">No appointments on this date.</p>
                        ) : filteredCalendarDayAppointments.length === 0 ? (
                          <p className="appointments-calendar-day-list-empty">
                            {calendarDayTab === 'upcoming' && 'No upcoming appointments on this date.'}
                            {calendarDayTab === 'past' && 'No past appointments on this date.'}
                            {calendarDayTab === 'missed_cancelled' && 'No missed or cancelled appointments on this date.'}
                            {calendarDayTab === 'reschedule_pending' && 'No reschedule pending appointments on this date.'}
                            {calendarDayTab === 'all' && 'No appointments on this date.'}
                          </p>
                        ) : (
                          <div className="appointments-table-wrapper">
                            <table className="appointments-table">
                              <thead>
                                <tr>
                                  <th>PATIENT</th>
                                  <th>DATE & TIME</th>
                                  <th>DURATION</th>
                                  <th>TYPE</th>
                                  <th>STATUS</th>
                                  <th>ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredCalendarDayAppointments.map((appointment) => {
                                  const patientName = appointment.patient_name || 'Unknown Patient';
                                  const initials = getInitials(patientName);
                                  const canJoin = canJoinCall(appointment.id);
                                  const joinMessage = getJoinMessage(appointment.id);
                                  const isLoadingJoin = loadingCanJoin.has(appointment.id);
                                  const isUpcoming = (() => {
                                    if (!appointment.appointment_date) return false;
                                    const aptDate = new Date(appointment.appointment_date);
                                    const durationMin = appointment.duration_minutes ?? appointment.duration ?? 30;
                                    const endTime = new Date(aptDate.getTime() + durationMin * 60 * 1000);
                                    return endTime > new Date();
                                  })();
                                  const showJoinButton = (appointment.status === 'scheduled' || appointment.status === 'confirmed') && isUpcoming;
                                  return (
                                    <tr key={appointment.id}>
                                      <td>
                                        <div className="patient-cell">
                                          <div className="patient-avatar">{initials}</div>
                                          <span className="patient-name">{patientName}</span>
                                        </div>
                                      </td>
                                      <td>
                                        <div className="appointment-datetime">
                                          {formatDateTime(appointment.appointment_date)}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="appointment-duration">
                                          {formatDuration(appointment.duration_minutes ?? appointment.duration)}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="appointment-type">
                                          {getConsultationTypeLabel(appointment.appointment_type)}
                                        </div>
                                      </td>
                                      <td>
                                        <span className={`status-badge ${getAppointmentStatusBadgeClass(appointment.status)}${appointment.status === 'reschedule_pending' ? ' status-badge-reschedule-pending' : ''}`}>
                                          {getAppointmentStatusLabel(appointment.status)}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="appointment-actions">
                                          {appointment.status === 'completed' ? (
                                            <button
                                              className="action-btn-view"
                                              onClick={() => navigate(`/appointments/${appointment.id}`)}
                                            >
                                              View Details
                                            </button>
                                          ) : (
                                            <>
                                              <div className="appointment-actions-row">
                                                <button
                                                  className="action-btn-edit"
                                                  onClick={() => handleEditAppointment(appointment.id)}
                                                >
                                                  Edit
                                                </button>
                                                {showJoinButton && (
                                                <button
                                                  className={`action-btn-join ${!canJoin ? 'disabled' : ''}`}
                                                  onClick={() => canJoin && navigate(`/video-conference/${appointment.id}`)}
                                                  disabled={!canJoin}
                                                >
                                                  <Video size={16} className="action-icon" aria-hidden />
                                                  Join Call
                                                </button>
                                                )}
                                              </div>
                                              {showJoinButton && (joinMessage || isLoadingJoin) && (
                                                <div className="join-call-container">
                                                  {!canJoin && joinMessage && !isLoadingJoin && (
                                                    <div className="join-call-note">{joinMessage}</div>
                                                  )}
                                                  {isLoadingJoin && (
                                                    <div className="join-call-note">Checking availability...</div>
                                                  )}
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
            {/* Tabs */}
            <div className="appointments-tabs">
              <button
                className={`appointment-tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                All
              </button>
              <button
                className={`appointment-tab ${activeTab === 'upcoming' ? 'active' : ''}`}
                onClick={() => setActiveTab('upcoming')}
              >
                Upcoming
              </button>
              <button
                className={`appointment-tab ${activeTab === 'past' ? 'active' : ''}`}
                onClick={() => setActiveTab('past')}
              >
                Past
              </button>
              <button
                className={`appointment-tab ${activeTab === 'missed_cancelled' ? 'active' : ''}`}
                onClick={() => setActiveTab('missed_cancelled')}
              >
                Missed & Cancelled
              </button>
              <button
                className={`appointment-tab ${activeTab === 'reschedule_pending' ? 'active' : ''}`}
                onClick={() => setActiveTab('reschedule_pending')}
              >
                Reschedule Pending
              </button>
            </div>

            {/* Appointments Table */}
              <div className="appointments-table-wrapper">
                <table className="appointments-table">
                  <thead>
                    <tr>
                      <th>PATIENT</th>
                      <th>DATE & TIME</th>
                      <th>DURATION</th>
                      <th>TYPE</th>
                      <th>STATUS</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                          No appointments found
                        </td>
                      </tr>
                    ) : (
                      appointments.map((appointment) => {
                        const patientName = appointment.patient_name || 'Unknown Patient';
                        const initials = getInitials(patientName);
                        const canJoin = canJoinCall(appointment.id);
                        const joinMessage = getJoinMessage(appointment.id);
                        const isLoadingJoin = loadingCanJoin.has(appointment.id);
                        const isUpcoming = (() => {
                          if (!appointment.appointment_date) return false;
                          const aptDate = new Date(appointment.appointment_date);
                          const durationMin = appointment.duration_minutes ?? appointment.duration ?? 30;
                          const endTime = new Date(aptDate.getTime() + durationMin * 60 * 1000);
                          return endTime > new Date();
                        })();
                        const showJoinButton = (activeTab === 'upcoming' || activeTab === 'all') &&
                          (appointment.status === 'scheduled' || appointment.status === 'confirmed') &&
                          isUpcoming;

                        return (
                          <tr key={appointment.id}>
                            <td>
                              <div className="patient-cell">
                                <div className="patient-avatar">{initials}</div>
                                <span className="patient-name">{patientName}</span>
                              </div>
                            </td>
                            <td>
                              <div className="appointment-datetime">
                                {formatDateTime(appointment.appointment_date)}
                              </div>
                            </td>
                            <td>
                              <div className="appointment-duration">
                                {formatDuration(appointment.duration_minutes ?? appointment.duration)}
                              </div>
                            </td>
                            <td>
                              <div className="appointment-type">
                                {getConsultationTypeLabel(appointment.appointment_type)}
                              </div>
                            </td>
                            <td>
                              <span className={`status-badge ${getAppointmentStatusBadgeClass(appointment.status)}${appointment.status === 'reschedule_pending' ? ' status-badge-reschedule-pending' : ''}`}>
                                {getAppointmentStatusLabel(appointment.status)}
                              </span>
                            </td>
                            <td>
                              <div className="appointment-actions">
                                {activeTab === 'past' && appointment.status === 'completed' ? (
                                  <button
                                    className="action-btn-view"
                                    onClick={() => navigate(`/appointments/${appointment.id}`)}
                                  >
                                    View Details
                                  </button>
                                ) : (
                                  <>
                                    <div className="appointment-actions-row">
                                      <button
                                        className="action-btn-edit"
                                        onClick={() => handleEditAppointment(appointment.id)}
                                      >
                                        Edit
                                      </button>
                                      {showJoinButton && (
                                                <button
                                                  className={`action-btn-join ${!canJoin ? 'disabled' : ''}`}
                                                  onClick={() => canJoin && navigate(`/video-conference/${appointment.id}`)}
                                                  disabled={!canJoin}
                                                >
                                                  <Video size={16} className="action-icon" aria-hidden />
                                                  Join Call
                                                </button>
                                      )}
                                    </div>
                                    {showJoinButton && (joinMessage || isLoadingJoin) && (
                                      <div className="join-call-container">
                                        {!canJoin && joinMessage && !isLoadingJoin && (
                                          <div className="join-call-note">
                                            {joinMessage}
                                          </div>
                                        )}
                                        {isLoadingJoin && (
                                          <div className="join-call-note">
                                            Checking availability...
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              </>
            )}
                  </section>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppointmentsPage;
